import { z } from 'zod'
import { oc } from '@orpc/contract'

export const PATIENT_PROFILE_IMAGE_MAX_FILE_BYTES = 10 * 1024 * 1024
export const PATIENT_PROFILE_IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
] as const
export const PATIENT_IDENTITY_IMAGE_MIME_TYPES = ['image/jpeg', 'image/jpg', 'image/png'] as const

export const profilePhotoSchema = z
  .file()
  .max(PATIENT_PROFILE_IMAGE_MAX_FILE_BYTES)
  .mime([...PATIENT_PROFILE_IMAGE_MIME_TYPES])

export const patientIdentityImageSchema = z
  .file()
  .max(PATIENT_PROFILE_IMAGE_MAX_FILE_BYTES)
  .mime([...PATIENT_IDENTITY_IMAGE_MIME_TYPES])

const patientGenderSchema = z.enum(['male', 'female', 'other', 'unknown'])
const addressUseSchema = z.enum(['home', 'work', 'temp', 'old', 'billing'])
const raceCodeSchema = z.enum(['1002-5', '2028-9', '2054-5', '2076-8', '2106-3'])

const profileNameSchema = z.object({
  given: z.array(z.string()),
  family: z.string().nullable(),
})

const profileAddressSchema = z.object({
  line: z.array(z.string()),
  city: z.string().nullable(),
  state: z.string().nullable(),
  postalCode: z.string().nullable(),
  use: addressUseSchema.nullable(),
})

const preferredLanguageSchema = z.object({
  code: z.enum(['en', 'es', 'other']),
  display: z.string().nullable(),
  text: z.string().nullable(),
})

const raceSchema = z.object({
  primary: raceCodeSchema.nullable(),
  secondary: raceCodeSchema.nullable(),
})

const ethnicitySchema = z.object({
  primary: z.array(z.string()),
  secondary: z.array(z.string()),
})

export const patientProfileSchema = z.object({
  name: profileNameSchema,
  birthDate: z.string().nullable(),
  sexAtBirth: z.enum(['male', 'female']).nullable(),
  gender: patientGenderSchema.nullable(),
  preferredLanguage: preferredLanguageSchema.nullable(),
  race: raceSchema,
  ethnicity: ethnicitySchema,
  mothersMaidenName: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  address: z.array(profileAddressSchema),
  patientDisplayId: z.string(),
  photoUrl: z.string().optional(),
})

const profileUpdateAddressSchema = z.object({
  line: z.array(z.string()),
  city: z.string().nullable().optional(),
  state: z.string().nullable().optional(),
  postalCode: z.string().nullable().optional(),
  use: addressUseSchema.nullable().optional(),
})

export const patientProfileGet = oc
  .route({ method: 'GET', path: '/v2/patient/me/profile' })
  .input(z.object({}))
  .output(patientProfileSchema)

export const patientProfileUpdate = oc
  .route({ method: 'POST', path: '/v2/patient/me/profile' })
  .input(
    z.object({
      name: profileNameSchema.optional(),
      birthDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'birthDate must be YYYY-MM-DD')
        .nullable()
        .optional(),
      sexAtBirth: z.enum(['male', 'female']).nullable().optional(),
      gender: patientGenderSchema.nullable().optional(),
      preferredLanguage: preferredLanguageSchema.nullable().optional(),
      race: raceSchema.optional(),
      ethnicity: ethnicitySchema.optional(),
      mothersMaidenName: z.string().nullable().optional(),
      address: z.array(profileUpdateAddressSchema).optional(),
      photo: profilePhotoSchema.nullable().optional(),
    }),
  )
  .output(patientProfileSchema)

const identityDocumentSchema = z.object({
  id: z.string(),
  uploadedAt: z.string().nullable(),
  contentType: z.string().nullable(),
  title: z.string().nullable(),
  url: z.string(),
  size: z.number().int().nonnegative().nullable(),
})

const identityDocumentResponseSchema = z.object({
  document: identityDocumentSchema.nullable(),
  identityVerified: z.boolean(),
  identityVerifiedAt: z.string().optional(),
  identityVerifiedBy: z.string().optional(),
})

export const patientIdentityDocumentGet = oc
  .route({ method: 'GET', path: '/v2/patient/me/identity-document' })
  .input(z.object({}))
  .output(identityDocumentResponseSchema)

export const patientIdentityDocumentUpload = oc
  .route({ method: 'POST', path: '/v2/patient/me/identity-document' })
  .input(z.object({ file: patientIdentityImageSchema }))
  .output(identityDocumentResponseSchema.extend({ supersedeFailed: z.boolean() }))
